import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import type { UnlinkEvent, UnlinkPreview } from '@uniticket/shared';
import { ApiError } from '@/lib/api-client';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { fetchUnlinkPreview, unlinkTicket } from '@/hooks/useAddressValidation';

// N31 (plan validé le 28-09) — « Délier ce JVID », ADMINS seulement. Le cas d'origine : UT-2026-001391, lié par
// erreur au JVID 10212432 le 24-09, sans aucun moyen de revenir en arrière.
//
// La fenêtre montre d'abord l'APERÇU calculé par le serveur — ce qui sera défait (le JVID, l'adresse, qui a lié et
// quand), ce que devient la ligne de file, les gestes faits depuis la liaison — ou pourquoi c'est refusé, en rouge :
// tout ce qui ne se défait pas en remettant les trois champs de la liaison est refusé, jamais défait en silence.
// Le motif est obligatoire (décision du 28-09) : c'est la seule trace de la raison du geste.
//
// Portail, comme BuildRepliedMarker : la fenêtre est ouverte depuis une GlassCard, dont le `backdrop-filter` ferait
// d'elle le bloc conteneur d'un `position: fixed`.
export function UnlinkTicketModal({ ticketId, open, onClose, onUnlinked }: {
  ticketId: string;
  open: boolean;
  onClose: () => void;
  onUnlinked: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [preview, setPreview] = useState<UnlinkPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadPreview() {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchUnlinkPreview(ticketId);
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      setLoadError((err as Error)?.message || t('tickets:validation.unlink.loadError'));
    } finally {
      setLoading(false);
    }
  }

  // Un aperçu frais à chaque ouverture : entre deux ouvertures, un collègue a pu écrire sur ce JVID.
  useEffect(() => {
    if (!open) return;
    setReason('');
    void loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  async function submit() {
    setBusy(true);
    try {
      await unlinkTicket(ticketId, reason.trim());
      toast.success(t('tickets:validation.unlink.done'));
      onClose();
      onUnlinked();
    } catch (err) {
      toast.error((err as Error)?.message || t('tickets:validation.unlink.error'));
      // 409 : un refus est apparu depuis l'aperçu (écriture DMP, ticket re-lié…) — le relire le montre.
      if (err instanceof ApiError && err.status === 409) void loadPreview();
    } finally {
      setBusy(false);
    }
  }

  const when = (iso: string) => new Date(iso).toLocaleString();
  const eventLine = (e: UnlinkEvent, i: number) => (
    <li key={`${e.at}-${i}`} className="text-[11px]">
      <span className="font-mono">{e.action}</span>
      {' · '}{when(e.at)}
      {e.by && <> · {e.by}</>}
    </li>
  );

  let body: ReactNode;
  if (loading && !preview) {
    body = (
      <p className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('tickets:validation.unlink.loading')}
      </p>
    );
  } else if (loadError || !preview) {
    body = <p className="text-rose-700">{loadError ?? t('tickets:validation.unlink.loadError')}</p>;
  } else {
    const p = preview;
    body = (
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1 text-left">
        <p>{t('tickets:validation.unlink.intro', { ticket: p.ticketNumber })}</p>
        {p.jvid != null && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <p className="font-semibold text-slate-800">
              {t('tickets:validation.unlink.jvid', { jvid: p.jvid })}
              {p.address && <span className="font-normal text-slate-600"> — {p.address}</span>}
            </p>
            {p.link && (
              <p className="mt-0.5">{t('tickets:validation.unlink.linkedBy', { by: p.link.by ?? '—', date: when(p.link.at) })}</p>
            )}
            {p.canUnlink && p.rowFate && (
              <p className="mt-0.5">
                {t(`tickets:validation.unlink.fate${p.rowFate}`, { jvid: p.jvid, ticket: p.restoreToTicketNumber ?? '—' })}
              </p>
            )}
          </div>
        )}
        {p.refusals.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
            <p className="flex items-center gap-1.5 font-bold">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {t('tickets:validation.unlink.refusedTitle')}
            </p>
            <ul className="mt-1 space-y-1.5">
              {p.refusals.map((r) => (
                <li key={r.code}>
                  {t(`tickets:validation.unlink.refusal.${r.code}`, { ...(r.params ?? {}), jvid: r.params?.jvid ?? p.jvid ?? '—' })}
                  {r.events && r.events.length > 0 && <ul className="mt-0.5 space-y-0.5 pl-3">{r.events.map(eventLine)}</ul>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {p.since.length > 0 && (
          <div className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600">
            <p className="flex items-center gap-1.5 font-semibold">
              <Info className="h-3.5 w-3.5 shrink-0" /> {t('tickets:validation.unlink.sinceTitle')}
            </p>
            <ul className="mt-1 space-y-0.5">{p.since.map(eventLine)}</ul>
          </div>
        )}
        {p.canUnlink && (
          <>
            {/* autoFocus : la fenêtre place le focus à son ouverture, quand l'aperçu n'est pas encore là — le champ
                du motif n'existe qu'une fois l'aperçu chargé, il se donne le focus lui-même en apparaissant. */}
            <textarea
              autoFocus
              className="w-full rounded-lg border border-slate-200 p-2 text-sm"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('tickets:validation.unlink.reasonPlaceholder')}
            />
            <p className="text-[11px] text-slate-400">{t('tickets:validation.unlink.traced')}</p>
          </>
        )}
      </div>
    );
  }

  return createPortal(
    <ConfirmDialog
      open={open}
      tone="rose"
      icon={<Unlink className="h-5 w-5" />}
      title={t('tickets:validation.unlink.title')}
      message={body}
      confirmLabel={t('tickets:validation.unlink.confirm')}
      cancelLabel={t('tickets:validation.unlink.cancel')}
      busy={busy}
      confirmDisabled={loading || !preview?.canUnlink || reason.trim().length < 3}
      onCancel={() => { if (!busy) onClose(); }}
      onConfirm={() => void submit()}
    />,
    document.body,
  );
}
